import { ActionReducer, createFeatureSelector, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';

import * as CpqActions from '../actions/cpq.actions';
import { IChange, ICpqSubmittedOrder } from '../models/cpq.models';
import { IShsEquipmentDataAttributes } from '../models/installedbase.models';
import { IAbstractProductIncluded } from '../models/abstract-product.models';

export interface CpqState {
  selectedInstalledBase: Partial<IShsEquipmentDataAttributes> | null;
  selectedSystem: Partial<IAbstractProductIncluded['attributes']> | null;
  error: unknown;
  changeLog: Array<IChange>;
  showChangeLogTab: boolean;
  undoLastOptionCommit: boolean;
  lastSubmittedOrder: ICpqSubmittedOrder | undefined;
}

export const initialState: CpqState = {
  selectedInstalledBase: null,
  selectedSystem: null,
  error: null,
  changeLog: [],
  showChangeLogTab: false,
  undoLastOptionCommit: false,
  lastSubmittedOrder: undefined
};

const getCpqFeatureState: MemoizedSelector<object, CpqState> = createFeatureSelector<CpqState>('cpq');

export const getSelectedInstalledBase: MemoizedSelector<object, Partial<IShsEquipmentDataAttributes> | null> = createSelector(
  getCpqFeatureState,
  state => state.selectedInstalledBase
);

export const getSelectedSystem: MemoizedSelector<object, Partial<IAbstractProductIncluded['attributes']> | null> = createSelector(
  getCpqFeatureState,
  state => state.selectedSystem
);

export const selectChangeLog: MemoizedSelector<object, Array<IChange>> = createSelector(
  getCpqFeatureState,
  state => state.changeLog
);

export const selectShowChangeLogTab: MemoizedSelector<object, boolean> = createSelector(
  getCpqFeatureState,
  state => state.showChangeLogTab
);

export const selectUndoLastOptionCommit: MemoizedSelector<object, boolean> = createSelector(
  getCpqFeatureState,
  state => state.undoLastOptionCommit
);

export const selectLastSubmittedOrder: MemoizedSelector<object, ICpqSubmittedOrder | undefined> = createSelector(
  getCpqFeatureState,
  state => state.lastSubmittedOrder
);

export const CpqReducer: ActionReducer<CpqState> = createReducer(
  initialState,
  on(CpqActions.setSelectedInstalledBase, (state, {selectedInstalledBase}) => {
    return {
      ...state,
      selectedInstalledBase
    };
  }),
  on(CpqActions.setSelectedSystem, (state, {selectedSystem}) => {
    return {
      ...state,
      selectedSystem: selectedSystem?.attributes ?? null
    };
  }),
  on(CpqActions.loadDataFailed, (state, {error}) => {
    return {
      ...state,
      error
    };
  }),
  on(CpqActions.updateChangeLog, (state, {changeLog}) => {
    return {
      ...state,
      changeLog
    };
  }),
  on(CpqActions.showChangeLogTab, (state) => {
    return {
      ...state,
      showChangeLogTab: !state.showChangeLogTab,
    };
  }),
  on(CpqActions.undoLastOptionCommit, (state) => {
    return {
      ...state,
      undoLastOptionCommit: !state.undoLastOptionCommit,
    };
  }),
  on(CpqActions.quoteSummaryPageActionAfterSubmittedOrder, (state, action) => {
    return {
      ...state,
      lastSubmittedOrder: action.order,
    };
  }),
  on(CpqActions.thankYouPageClearLastSubmittedOrder, (state) => {
    return {
      ...state,
      lastSubmittedOrder: undefined,
    };
  }),
);
